import * as React from 'react';
import * as _ from 'lodash';
import * as actions from './actions';
import {
  FilterValuesType,
  RowDataType,
  TablesState,
  TableState,
  VisibleColumnsType,
  VisibleColumnsPresetsType,
  ColumnsGroups,
} from './actions';
import {AsyncVirtualizedProps} from '../AsyncVirtualized';
import {IndexRange} from 'react-virtualized';
import {VisibleColumnsPresetsGetter} from '../consts';
import {Persist} from 'react-persist';
import {getUpdatedData, getVisibleColumns} from './selectors';
import connectWithRouter from '../../../shared/components/redux/connectWithRouter';
import {Location} from 'history';
import {Overwrite} from 'utility-types';
import {TableFilterProps} from '../TableFilter/TableFilter';
import {PlainRoute} from 'react-router';
import {getConfirmationMessage} from '../../../shared/helpers/getConfirmationMessage';
import {getCurrentState} from '../../../shared/ActionsManager/EbayListingsActions/currentState';
import memoize from 'memoize-one';
import {updateDataIfHasChildren} from '../VirtualizedHelpers';

export enum TableDataSource {
  INTERNAL, // all data will be loaded at once.
  EXTERNAL,
  EXTERNAL_LOADED,
}

export type ReduxTableOptions<RowData extends RowDataType = RowDataType> = {
  name: string;
  getInitialState(initParams?: any): Partial<TableState<RowData>> | Promise<Partial<TableState<RowData>>>;
  getRefreshedColumns?(): Promise<TableState<RowData>['columns']>;
  loadMoreRows?(
    params: IndexRange,
    state: TableState<RowData>
  ): Promise<{rows: RowData[]; count?: number; additionalData?: any}>;
  props?: Partial<Overwrite<WrappedComponentProps<RowData>, {filterProps: Partial<TableFilterProps>}>>;
  onChange?: (
    selection: TableState['selection'],
    columnName: string,
    value: number | string,
    state: TableState<RowData>
  ) => void;
  getConfig?(key: string): object | Promise<object>;
  dataSource?: TableDataSource;
  onFilterChange?: (filter: FilterValuesType) => void;
  readOnly?: boolean;
  getColumnChooserGroups?(tableState: TableState<RowData>): ColumnsGroups;
  data?: TableState['data'];
  showAllVisibleColumns?: boolean;
};

export interface TableContext {
  name: string;
  tableOptions: ReduxTableOptions;
  loadMoreRows?(params: IndexRange, reset?: boolean);
  onRequestSort(sortProps);
  onChange(selection, columnName, value);
  onRowsRendered(params);
  onToggleSelect(index: number);
  onToggleSelectAll();
  onFilterChange(filter: FilterValuesType): void;
  onClearFilter(): void;
  onVisibleColumnsChange(visibleColumns: VisibleColumnsType);
  onSetFilterOpen(open: boolean);
  getVisibleColumnsPresets: VisibleColumnsPresetsGetter;
  getColumnChooserGroups(): ColumnsGroups;
}
export const TableStateContext = React.createContext<TableState>(null);
const {Provider: StateProvider, Consumer: StateConsumer} = TableStateContext;
export const TableContextContext = React.createContext<TableContext>(null);
const {Provider, Consumer: ContextConsumer} = TableContextContext;
export const TableStateConsumer = StateConsumer;
export const TableContextConsumer = ContextConsumer;

export interface WrappedComponentProps<RowData extends RowDataType> extends AsyncVirtualizedProps<RowData> {}

export default function<RowData extends RowDataType>(tableOptions: ReduxTableOptions<RowData>) {
  const {
    name,
    getInitialState,
    loadMoreRows,
    onChange,
    getConfig,
    dataSource = TableDataSource.EXTERNAL,
    getColumnChooserGroups,
    readOnly,
    showAllVisibleColumns,
    onFilterChange,
    getRefreshedColumns,
    props: {supportChildren},
  } = tableOptions;
  type ReduxTableProps = TableState<RowData> &
    typeof actions & {routes?: PlainRoute[]; router?: any; onTableRefresh(): any};
  const reduxTable = class ReduxTableContext extends React.Component<ReduxTableProps> implements TableContext {
    public name = name;
    public tableOptions = tableOptions;
    async componentWillMount() {
      const {setInitialTableState, initParams, data, columns} = this.props;
      const tableState = {initParams, ...(await getInitialState(initParams || {}))};
      if (data && !tableState.data) {
        tableState.data = data;
        tableState.count = data.size;
      }
      if (columns && !tableState.columns) {
        tableState.columns = columns;
      }
      setInitialTableState({name, state: tableState, showAllVisibleColumns});
      switch (dataSource) {
        case TableDataSource.INTERNAL:
          await this.loadMoreRows({startIndex: 0, stopIndex: tableState.count - 1});
          break;
        case TableDataSource.EXTERNAL:
          await this.loadMoreRows({startIndex: 0, stopIndex: 15}, true);
          break;
        default: // Do nothing
      }
      if (!data) {
        this.props.router.setRouteLeaveHook(_.last(this.props.routes), this.promptUserForUnsavedChanges);
        window.addEventListener('beforeunload', this.promptUserForUnsavedChanges);
      }
    }

    async componentDidUpdate(prevProps: ReduxTableProps) {
      const {
        visibleRange,
        tableRefresh,
        clearTableRefresh,
        data,
        setData,
        columns,
        setColumns,
        virtualized,
        onTableRefresh,
      } = this.props;
      if (tableRefresh && !prevProps.tableRefresh) {
        clearTableRefresh({name});
        if (onTableRefresh) {
          onTableRefresh();
        }
        const startIndex = visibleRange ? visibleRange.startIndex : 0;
        const promise = this.loadMoreRows({startIndex: startIndex, stopIndex: startIndex + 15}, true);
        if (getRefreshedColumns) {
          setColumns({name, columns: await getRefreshedColumns()});
        }
        await promise;
      }
      if (!virtualized) {
        if (prevProps.data !== data) {
          setData({name, data});
        }
        if (prevProps.columns !== columns) {
          setColumns({name, columns, resetVisibleColumns: true});
        }
      }
    }

    componentWillUnmount() {
      const {deleteTable} = this.props;
      window.removeEventListener('beforeunload', this.promptUserForUnsavedChanges);
      deleteTable({name});
    }

    public loadMoreRows = async (params: IndexRange, reset?: boolean) => {
      const {setRows} = this.props;
      const {startIndex} = params;
      const data = await Promise.resolve(loadMoreRows(params, this.props));
      const {rows, count, additionalData} = data;
      setRows({name, startIndex, rows, reset, count, additionalData, supportChildren});
    };

    public onRequestSort =
      dataSource === TableDataSource.EXTERNAL || dataSource === TableDataSource.EXTERNAL_LOADED
        ? sortProps => {
            const {setSortProps} = this.props;
            setSortProps({name, sortProps});
          }
        : null;

    public onChange = !readOnly
      ? async (selection, columnName, value) => {
          const {updateRows} = this.props;
          if (onChange) {
            await onChange(selection, columnName, value, this.props);
          } else {
            updateRows({name, columnName, selection: selection, updater: _.constant(value)});
          }
        }
      : null;

    public onRowsRendered = _.debounce(
      params => {
        const {setVisibleRange, visibleRange} = this.props;
        if (!_.isEqual(params, visibleRange)) {
          setVisibleRange({name, visibleRange: params});
        }
      },
      500,
      {leading: false, trailing: true}
    );

    public onToggleSelect = (index: number) => {
      const {toggleSelect} = this.props;
      toggleSelect({name, index});
    };

    public onToggleSelectAll = () => {
      const {toggleSelectAll} = this.props;
      toggleSelectAll({name});
    };

    public onFilterChange = (filter: FilterValuesType) => {
      const {setFilter} = this.props;
      setFilter({name, filter});
      if (_.isFunction(onFilterChange)) {
        onFilterChange(filter);
      }
    };

    public onSetFilterOpen = (open: boolean) => {
      const {setFilterOpen} = this.props;
      setFilterOpen({name, open});
    };

    public onClearFilter = () => {
      const {clearFilter} = this.props;
      clearFilter({name});
    };

    public onVisibleColumnsChange = (visibleColumns: VisibleColumnsType) => {
      const {setVisibleColumns, columns} = this.props;
      setVisibleColumns({name, visibleColumns: _.filter(visibleColumns, columnName => columns[columnName])});
    };

    public getVisibleColumnsPresets: VisibleColumnsPresetsGetter = () => {
      if (getConfig) {
        return getConfig(`${name}.visibleColumnsPresets`) as VisibleColumnsPresetsType;
      }
      return null;
    };

    getColumnChooserGroups = () => {
      if (getColumnChooserGroups) {
        return getColumnChooserGroups(this.props);
      }
      return null;
    };

    private renderPersist() {
      return (
        <React.Fragment>
          {this.props.visibleColumns ? (
            <Persist
              name={`${name}.visibleColumns`}
              data={this.props.visibleColumns}
              onMount={this.onVisibleColumnsChange}
            />
          ) : (
            undefined
          )}
        </React.Fragment>
      );
    }

    private promptUserForUnsavedChanges = event => {
      const {dataUpdates} = this.props;
      if (getCurrentState().table[name] && dataUpdates && dataUpdates.size > 0) {
        return getConfirmationMessage(event);
      }
      return undefined;
    };

    private getTableContext = memoize(() => {
      if (dataSource === TableDataSource.INTERNAL) {
        return _.omit(this, 'loadMoreRows') as TableContext;
      }
      return this;
    });

    render() {
      return (
        <StateProvider value={this.props}>
          <Provider value={this.getTableContext()}>
            {this.renderPersist()}
            {this.props.children}
          </Provider>
        </StateProvider>
      );
    }
  };
  return connectWithRouter((state, ownProps) => {
    const {table, routing}: {table: TablesState<RowData>; routing: {location: Location}} = state;
    const tableState: TableState<RowData> = table[name] || ({} as TableState<RowData>);
    const {data, columns} = ownProps;
    return {
      initParams: routing.location.query.initParams ? JSON.parse(routing.location.query.initParams) : undefined,
      ...tableState,
      data:
        data && tableState.columnsConfig
          ? updateDataIfHasChildren(data, tableState.columnsConfig, supportChildren)
          : getUpdatedData(tableState),
      ...(columns && {
        columns: columns,
        visibleColumns: getVisibleColumns({...tableState, columns}),
      }),
      ...(data && {count: data.size}),
    };
  }, actions)(reduxTable);
}
